import { useEffect, useRef, useState } from "react";
import { FiMaximize, FiMinimize, FiPause, FiPlay, FiVolume2, FiVolumeX } from "react-icons/fi";
import { MdFastForward, MdReplay } from "react-icons/md";

const SHORT_VIDEO_MAX_SECONDS = 15;
const SHORT_SKIP_SECONDS = 3;
const LONG_SKIP_SECONDS = 5;

const skipSecondsForDuration = (duration) =>
  Number.isFinite(duration) && duration > SHORT_VIDEO_MAX_SECONDS
    ? LONG_SKIP_SECONDS
    : SHORT_SKIP_SECONDS;

const formatTime = (totalSeconds) => {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "0:00";
  const seconds = Math.floor(totalSeconds);
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}:${String(remainder).padStart(2, "0")}`;
};

const timeFromClientX = (clientX, element, duration) => {
  const rect = element.getBoundingClientRect();
  if (!rect.width) return { time: 0, percent: 0 };
  const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  const safeDuration = Number.isFinite(duration) && duration > 0 ? duration : 0;
  return { time: ratio * safeDuration, percent: ratio * 100 };
};

const HelpManualVideoPlayer = ({ src, fileName, sizeLabel, compact = false }) => {
  const videoRef = useRef(null);
  const shellRef = useRef(null);
  const seekWrapRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [skipSeconds, setSkipSeconds] = useState(SHORT_SKIP_SECONDS);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [seekTip, setSeekTip] = useState(null);
  const [isSeeking, setIsSeeking] = useState(false);

  const updateSkipSeconds = () => {
    const nextDuration = videoRef.current?.duration ?? 0;
    setSkipSeconds(skipSecondsForDuration(nextDuration));
  };

  const syncTime = () => {
    const video = videoRef.current;
    if (!video) return;
    setCurrentTime(video.currentTime);
    setDuration(Number.isFinite(video.duration) ? video.duration : 0);
  };

  useEffect(() => {
    setIsPlaying(false);
    setHasError(false);
    setSkipSeconds(SHORT_SKIP_SECONDS);
    setCurrentTime(0);
    setDuration(0);
    setIsMuted(false);
    setSeekTip(null);
    setIsSeeking(false);
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
      video.muted = false;
    }
  }, [src]);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === shellRef.current);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video || !src || hasError) return;
    if (video.paused) {
      video.play().catch(() => setHasError(true));
    } else {
      video.pause();
    }
  };

  const skipVideo = (direction) => {
    const video = videoRef.current;
    if (!video || !src || hasError) return;
    const nextSkip = skipSecondsForDuration(video.duration);
    setSkipSeconds(nextSkip);
    const nextDuration = Number.isFinite(video.duration) ? video.duration : 0;
    const nextTime = video.currentTime + direction * nextSkip;
    video.currentTime = Math.min(
      Math.max(0, nextTime),
      nextDuration || Math.max(0, nextTime)
    );
    syncTime();
  };

  const seekVideo = (event) => {
    const video = videoRef.current;
    if (!video || hasError) return;
    const value = Number(event.target.value);
    video.currentTime = value;
    syncTime();
    const percent = duration > 0 ? (value / duration) * 100 : 0;
    setSeekTip({ time: value, percent });
  };

  const updateSeekTipFromPointer = (event) => {
    const wrap = seekWrapRef.current;
    if (!wrap) return;
    setSeekTip(timeFromClientX(event.clientX, wrap, duration));
  };

  const onSeekPointerDown = (event) => {
    setIsSeeking(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    updateSeekTipFromPointer(event);
  };

  const onSeekPointerMove = (event) => {
    updateSeekTipFromPointer(event);
  };

  const onSeekPointerUp = (event) => {
    setIsSeeking(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    const wrap = seekWrapRef.current;
    if (!wrap) {
      setSeekTip(null);
      return;
    }
    const rect = wrap.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
    if (!inside) {
      setSeekTip(null);
    }
  };

  const onSeekPointerLeave = () => {
    if (!isSeeking) {
      setSeekTip(null);
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video || hasError) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const toggleFullscreen = () => {
    const shell = shellRef.current;
    if (!shell) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
      return;
    }
    void shell.requestFullscreen();
  };

  const onPlayerKeyDown = (event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      skipVideo(-1);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      skipVideo(1);
    } else if (event.key === " ") {
      event.preventDefault();
      togglePlay();
    }
  };

  return (
    <div className={`help-manual-video-card ${compact ? "help-manual-video-card--compact" : ""}`}>
      {(fileName || sizeLabel) && (
        <div className="flex items-center justify-between gap-3 px-3 py-2 bg-black text-white text-xs">
          <span className="truncate font-medium">{fileName}</span>
          {sizeLabel ? (
            <span className="shrink-0 text-gray-300">{sizeLabel}</span>
          ) : null}
        </div>
      )}

      <div
        ref={shellRef}
        className={`help-manual-video-player ${
          compact ? "help-manual-video-player--compact" : ""
        }`}
      >
        <video
          ref={videoRef}
          src={src || undefined}
          controlsList="nodownload noremoteplayback"
          disablePictureInPicture
          disableRemotePlayback
          preload="metadata"
          playsInline
          tabIndex={0}
          width="100%"
          height="100%"
          onLoadedMetadata={() => {
            updateSkipSeconds();
            syncTime();
          }}
          onTimeUpdate={syncTime}
          onContextMenu={(event) => event.preventDefault()}
          onDragStart={(event) => event.preventDefault()}
          onClick={togglePlay}
          onKeyDown={onPlayerKeyDown}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
          onVolumeChange={() => setIsMuted(Boolean(videoRef.current?.muted))}
          onError={() => setHasError(true)}
        >
          Your browser does not support video playback.
        </video>

        {src && !isPlaying && !hasError && (
          <button
            type="button"
            className="help-manual-video-play"
            onClick={(event) => {
              event.stopPropagation();
              togglePlay();
            }}
            aria-label="Play video"
          >
            <span className="help-manual-video-play__icon">
              <FiPlay className="w-6 h-6 ml-0.5" />
            </span>
          </button>
        )}

        {src && !hasError && (
          <div
            className="help-manual-video-controls"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="help-manual-video-ctrl"
              onClick={togglePlay}
              aria-label={isPlaying ? "Pause video" : "Play video"}
            >
              {isPlaying ? <FiPause size={16} /> : <FiPlay size={16} />}
            </button>
            <button
              type="button"
              className="help-manual-video-ctrl"
              onClick={() => skipVideo(-1)}
              aria-label={`Go back ${skipSeconds} seconds`}
              title={`Go back ${skipSeconds} seconds`}
            >
              <MdReplay size={16} />
              <span>{skipSeconds}s</span>
            </button>
            <label
              ref={seekWrapRef}
              className="help-manual-video-seek-wrap"
              onPointerDown={onSeekPointerDown}
              onPointerMove={onSeekPointerMove}
              onPointerUp={onSeekPointerUp}
              onPointerCancel={onSeekPointerUp}
              onPointerLeave={onSeekPointerLeave}
            >
              {seekTip && (
                <span
                  className="help-manual-video-seek-tip"
                  style={{ left: `${Math.min(92, Math.max(8, seekTip.percent))}%` }}
                  role="status"
                >
                  {formatTime(seekTip.time)} / {formatTime(duration)}
                </span>
              )}
              <input
                className="help-manual-video-seek"
                type="range"
                min="0"
                step="0.1"
                max={duration || 0}
                value={currentTime}
                onChange={seekVideo}
                aria-label="Video timeline"
                aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
              />
            </label>
            <button
              type="button"
              className="help-manual-video-ctrl"
              onClick={() => skipVideo(1)}
              aria-label={`Go forward ${skipSeconds} seconds`}
              title={`Go forward ${skipSeconds} seconds`}
            >
              <span>{skipSeconds}s</span>
              <MdFastForward size={16} />
            </button>
            <span className="help-manual-video-time">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
            <button
              type="button"
              className="help-manual-video-ctrl"
              onClick={toggleMute}
              aria-label={isMuted ? "Unmute video" : "Mute video"}
            >
              {isMuted ? <FiVolumeX size={16} /> : <FiVolume2 size={16} />}
            </button>
            <button
              type="button"
              className="help-manual-video-ctrl"
              onClick={toggleFullscreen}
              aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            >
              {isFullscreen ? <FiMinimize size={16} /> : <FiMaximize size={16} />}
            </button>
          </div>
        )}

        {hasError && (
          <div className="help-manual-video-error">
            Video could not be played in this browser.
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpManualVideoPlayer;
